import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { PURGE } from "redux-persist";
import apiInstance from '../../../../api';

export const getModerationReviews = createAsyncThunk(
    'moderationReviews/getModerationReviews',
    async (data, { rejectWithValue }) => {
        const api = '/reviews/get?XDEBUG_SESSION_START=MyVSCode';

        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));
    
            if (response.data.result) {
                return {
                    items: response.data.data.reviews,
                    total: response.data.data.pagination.total,
                };
            } else {
                return rejectWithValue('Failed to fetch data');
            }
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

export const setModerationReviewProcessed = createAsyncThunk(
    'moderationReviews/setModerationReviewProcessed',
    async (data, { rejectWithValue }) => {
        const api = '/reviews/setProcessed?XDEBUG_SESSION_START=MyVSCode';

        try {
            const response = await apiInstance.post(api, JSON.stringify({ data }));

            if (response.data.result) {
                const nextStatus = response?.data?.data?.status ?? data?.status;

                return {
                    orderId: data.orderId,
                    processedAt: response?.data?.data?.processedAt || null,
                    status: nextStatus,
                    comment: response?.data?.data?.comment || data?.comment || '',
                };
            }

            return rejectWithValue('Failed to set review as processed');
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

export const sendModerationReviewPush = createAsyncThunk(
    'moderationReviews/sendModerationReviewPush',
    async (data, { rejectWithValue }) => {
        const api = '/customerNotifications/selectDevices?XDEBUG_SESSION_START=MyVSCode';

        const formData = new FormData();
        formData.append('data', JSON.stringify(data));

        try {
            const response = await apiInstance.post(api, formData);

            if (response.data.result) {
                return response?.data?.data || {};
            }

            return rejectWithValue('Failed to send push notification');
        } catch (error) {
            console.error('Ошибка при выполнении запроса:', error);
            return rejectWithValue(error?.response?.data?.message || 'Something went wrong');
        }
    }
);

const initialState = {
    getReviewsStatus: null,
    setReviewProcessedStatus: null,
    sendReviewPushStatus: null,
    reviews: [],
    total: 0,
    pageSize: 50,
    currentPage: 1,
};

const moderationReviewsSlice = createSlice({
    name: 'moderationReviews',
    initialState,
    extraReducers: (builder) => {
        builder
            // Fetch Data
            .addCase(getModerationReviews.pending, (state) => {
                state.getReviewsStatus = 'pending';
            })
            .addCase(getModerationReviews.fulfilled, (state, action) => {
                state.getReviewsStatus = 'fulfilled';
                const { items, total } = action.payload;
                state.reviews = items;
                state.total = total;
            })            
            .addCase(getModerationReviews.rejected, (state) => {
                state.getReviewsStatus = 'rejected';
            })
            .addCase(setModerationReviewProcessed.pending, (state) => {
                state.setReviewProcessedStatus = 'pending';
            })
            .addCase(setModerationReviewProcessed.fulfilled, (state, action) => {
                state.setReviewProcessedStatus = 'fulfilled';

                state.reviews = state.reviews.map((review) => {
                    if (review.orderId !== action.payload.orderId) {
                        return review;
                    }

                    return {
                        ...review,
                        admin: {
                            ...(review.admin || {}),
                            status: action.payload.status ?? review?.admin?.status,
                            statusLabel: action.payload.statusLabel || review?.admin?.statusLabel,
                            processedAt: action.payload.processedAt ?? review?.admin?.processedAt ?? null,
                            comment: action.payload.comment ?? review?.admin?.comment ?? '',
                        },
                    };
                });
            })
            .addCase(setModerationReviewProcessed.rejected, (state) => {
                state.setReviewProcessedStatus = 'rejected';
            })
            .addCase(sendModerationReviewPush.pending, (state) => {
                state.sendReviewPushStatus = 'pending';
            })
            .addCase(sendModerationReviewPush.fulfilled, (state) => {
                state.sendReviewPushStatus = 'fulfilled';
            })
            .addCase(sendModerationReviewPush.rejected, (state) => {
                state.sendReviewPushStatus = 'rejected';
            })
            
            // PURGE
            .addCase(PURGE, () => {
                return initialState;
            });
    },
    reducers: {
        setPageSize: (state, action) => {
            state.pageSize = action.payload;
        },
        setCurrentPage: (state, action) => {
            state.currentPage = action.payload;
        },
    },
});

export const {
    setPageSize,
    setCurrentPage,
} = moderationReviewsSlice.actions;

export default moderationReviewsSlice.reducer;
